<template>
  <div class="form">
    <div class="form__content">
      <h1 class="extra-large-headline">Source of funds questionnaire.</h1>
      <p class="bodycopy">
        Before we process your order, we’re legally obligated to collect the
        additional information below.
      </p>
    </div>
    <form @submit.prevent>
      <AutocompleteSelect
        v-if="showCountrySelect"
        v-model:autocomplete="form.country"
        :label="'Country of residence*'"
        :placeholder="'Search a country'"
        :type="'text'"
        :items="countryList"
        :errors="sourceOfFundsStore.getErrorByField('country')"
        hasIcon
      />

      <FormSelect
        name="employmentStatus"
        label="Employment status"
        type="text"
        :items="sofFormAnswers.employmentStatus"
        @dropdownValueUpdate="updateDropdown"
      />
      <FormSelect
        name="industryType"
        label="Industry you work in"
        type="text"
        :items="sofFormAnswers.industryType"
        @dropdownValueUpdate="updateDropdown"
      />
      <FormSelect
        name="beneficiaries"
        label="Besides yourself, are there any other individuals / beneficiaries who will benefit or have access to this account?"
        type="text"
        :items="sofFormAnswers.beneficiaries"
        @dropdownValueUpdate="updateDropdown"
      />
      <FormSelect
        name="yearlyAmount"
        label="How much cryptocurrency do you anticipate buying per year using our service?"
        type="text"
        :items="sofFormAnswers.yearlyAmount"
        @dropdownValueUpdate="updateDropdown"
      />

      <FormSelect
        name="potMainPurpose"
        label="Describe the purpose of your transactions."
        type="text"
        :items="sofFormAnswers.potMainPurpose"
        @dropdownValueUpdate="updateDropdown"
      />
      <FormSelect
        :maxItems="3"
        :isMultiSelect="true"
        name="originOfFunds"
        label="Describe the origin of the financial resources you have used or will be using to conduct transactions with us"
        type="text"
        :items="sofFormAnswers.originOfFunds"
        @dropdownValueUpdate="updateDropdown"
      />

      <Checkbox
        name="tnc"
        text="I declare that the information I have provided on the Source of Funds Questionnaire is true and complete. I also agree to provide supporting documents as proof of funds upon request by Banxa."
        v-model:value="form.tnc"
        v-model:tnc="form.tnc"
        :errors="sourceOfFundsStore.getErrorByField('tnc')"
      />

      <ButtonBase
        :isDisabled="
          sourceOfFundsStore.formHasErrors || globalStore.getIsFormLoading
        "
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        Continue
      </ButtonBase>
    </form>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";
import { useSourceOfFundsFormStore } from "@/stores/forms/sourceOfFundsFormStore";
import { useVerificationStore } from "@/stores/forms/verificationFormStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import useApiHelpers from "@/composables/api/helpers";
import useCustomerActions from "@/composables/api/useCustomerActions";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useGlobalCountriesActions from "@/composables/store/global/useGlobalCountriesActions.js";

import FormSelect from "@/components/ui/selects/FormSelect.vue";
import Checkbox from "@/components/ui/inputs/Checkbox.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import AutocompleteSelect from "@/components/ui/selects/AutocompleteSelect.vue";

const globalStore = useGlobalStore();
const sourceOfFundsStore = useSourceOfFundsFormStore();
const verificationStore = useVerificationStore();
const identityQueueStore = useIdentityQueueStore();
const { fetchCountries } = useGlobalCountriesActions();
const { postRequest } = useApiHelpers();
const { fetchCustomerId } = useCustomerActions();
const { watchAndUpdateStore } = useWatchAndUpdate();
const router = useRouter();
const countryList = ref([]);
// todo mocked for now, to get from an endpoint
const sofFormAnswers = ref({
  employmentStatus: [
    "Self-employed",
    "Employed",
    "Retired",
    "Unemployed",
    "Full time students",
  ],
  industryType: [
    "Agriculture, Food and Natural Resources",
    "Construction",
    "Architecture",
    "Arts, Audio/Video Technology and Communications",
    "Business Management and Adminstration",
    "Education and Training",
    "Finance",
    "Government and Public Adminstration",
    "Health and Science (eg. medical personnel",
    "Hospitality and Tourism",
    "Human Services (eg. social workers)",
    "Information Technology",
    "Law, Public Safety, Connections and Security",
    "Manufacturing",
    "Marketing, Sales and Service",
    "Non-Governmental Organistion/Charity",
    "Professional Service Provider/Consulting",
    "Real Estate",
    "Science, Technology, Engineering and Mathematics",
  ],
  beneficiaries: ["Yes", "No"],
  yearlyAmount: [
    "Up to 15,000",
    "15,000 to 30,000",
    "30,000 to 50,000",
    "50,000 to 70,000",
    "70,000 to 100,000",
    "100,000 and more",
  ],
  potMainPurpose: [
    "Investment",
    "Trading",
    "Payment for online purchases",
    "Payment for services",
    "Gaming",
    "Other",
    "Ransomware / virus/ cyber extortion demands",
  ],
  originOfFunds: [
    "Personal Savings",
    "Salary",
    "Business Income/Profits",
    "Pension",
    "Givernment Benefits",
    "Income from Cryptocurrency Trading",
    "Income from Investments (eg. shares, bonds, investment funds)",
    "Income from sale of real estate or other property",
    "Income from renting/leasing",
    "Private loan from family members / friends / others",
    "Loan from bank",
    "Inheritance",
    "Gift",
    "Other",
  ],
});
const potMainPurposeAnswers = [
  "Investment",
  "Trading",
  "Payment for online purchases",
  "Payment for services",
  "Gaming",
  "Other",
  "Ransomware / virus/ cyber extortion demands",
  "Other",
];

const showCountrySelect = ref(false);
const form = ref({
  country:
    (verificationStore.form.country.value &&
      globalStore.getCountryCode(verificationStore.form.country.value)) ||
    "",
  employmentStatus: sourceOfFundsStore.form.employmentStatus.value,
  industryType: sourceOfFundsStore.form.industryType.value,
  beneficiaries: sourceOfFundsStore.form.beneficiaries.value,
  yearlyAmount: sourceOfFundsStore.form.yearlyAmount.value,
  potMainPurpose: sourceOfFundsStore.form.potMainPurpose.value,
  originOfFunds: sourceOfFundsStore.form.originOfFunds.value,
  tnc: sourceOfFundsStore.form.tnc.value || false,
});

const updateDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  form.value[field] = value;
};

const handleFormSubmit = async () => {
  globalStore.setIsFormLoading(true);
  const custId = await fetchCustomerId();

  const formData = {
    documentType: "source_of_funds",
    documentReference: custId,
    declaredSourceOfFunds: {
      country_of_residence: form.value.country,
      employment_status: form.value.employmentStatus,
      employment_industry: form.value.industryType,
      expected_amount: form.value.yearlyAmount,
      purpose_of_transaction: [form.value.potMainPurpose],
      origin_of_funds: [form.value.originOfFunds],
    },
  };

  postRequest("documents/edd", formData, () =>
    router.push(identityQueueStore.getNextIdentityRoute)
  );
};

watchAndUpdateStore(form, sourceOfFundsStore, () => {});

onMounted(async () => {
  const formattedCountries = await fetchCountries();
  countryList.value = formattedCountries;
  showCountrySelect.value = !form.value.country;
});
</script>
